import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toCsv, downloadCsv } from "@/lib/csv";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, AreaChart, Area,
} from "recharts";

// ---------- types ----------
export type FunnelRow = {
  id: string;
  name: string;
  campaign: string | null;
  placement: string | null;
  destination: string | null;
  slot_start: string | null;
  studio_id: string | null;
  session_id: string | null;
  user_id: string | null;
  params: Record<string, unknown>;
  created_at: string;
};

export type StepDef = { key: string; label: string; color: string };

type Props = {
  campaign: string;
  title: string;
  /** Steps in funnel order. Defaults to `<campaign>_cta_click`, checkout_initiated, checkout_completed. */
  steps?: StepDef[];
};

const DEFAULT_COLORS = ["hsl(var(--primary))", "#7dd3fc", "#a3e635"];
const todayMinus = (d: number) =>
  new Date(Date.now() - d * 86400_000).toISOString().slice(0, 10);

function num(v: unknown): number {
  const n = typeof v === "string" ? Number(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : 0;
}
function revenueOf(r: FunnelRow): number {
  const p = r.params || {};
  return num(
    (p as any).booking_total ??
      (p as any).grand_total ??
      (p as any).value ??
      (p as any).amount ??
      0,
  );
}

// ---------- component ----------
export function FunnelDashboard({ campaign, title, steps }: Props) {
  const resolvedSteps: StepDef[] = useMemo(
    () =>
      steps ?? [
        { key: `${campaign}_cta_click`, label: "CTA click", color: DEFAULT_COLORS[0] },
        { key: "checkout_initiated", label: "Initiated", color: DEFAULT_COLORS[1] },
        { key: "checkout_completed", label: "Completed", color: DEFAULT_COLORS[2] },
      ],
    [campaign, steps],
  );
  const stepKeys = resolvedSteps.map((s) => s.key);

  const [from, setFrom] = useState(todayMinus(30));
  const [to, setTo] = useState(todayMinus(0));
  const [placement, setPlacement] = useState<string>("__all");
  const [destination, setDestination] = useState<string>("__all");
  const [studioId, setStudioId] = useState<string>("__all");
  const [sessionQ, setSessionQ] = useState<string>("");
  const [rows, setRows] = useState<FunnelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState<FunnelRow | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("funnel_events")
      .select("*")
      .eq("campaign", campaign)
      .gte("created_at", `${from}T00:00:00Z`)
      .lte("created_at", `${to}T23:59:59Z`)
      .order("created_at", { ascending: false })
      .limit(10000);
    if (!error) setRows((data ?? []) as FunnelRow[]);
    setLoading(false);
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  // Distinct filter options
  const placements = useMemo(() => uniq(rows.map((r) => r.placement)), [rows]);
  const destinations = useMemo(() => uniq(rows.map((r) => r.destination)), [rows]);
  const studios = useMemo(() => uniq(rows.map((r) => r.studio_id)), [rows]);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (placement !== "__all" && (r.placement ?? "") !== placement) return false;
        if (destination !== "__all" && (r.destination ?? "") !== destination) return false;
        if (studioId !== "__all" && (r.studio_id ?? "") !== studioId) return false;
        if (sessionQ && !(r.session_id ?? "").toLowerCase().includes(sessionQ.toLowerCase()))
          return false;
        return true;
      }),
    [rows, placement, destination, studioId, sessionQ],
  );

  // Totals per step
  const totals = useMemo(() => {
    const t: Record<string, number> = Object.fromEntries(stepKeys.map((k) => [k, 0]));
    for (const r of filtered) if (r.name in t) t[r.name]++;
    return t;
  }, [filtered, stepKeys]);

  // Revenue (sum of checkout_completed payloads)
  const revenue = useMemo(
    () =>
      filtered
        .filter((r) => r.name === "checkout_completed")
        .reduce((s, r) => s + revenueOf(r), 0),
    [filtered],
  );
  const aov =
    totals.checkout_completed > 0 ? revenue / totals.checkout_completed : 0;

  // Daily series
  const byDay = useMemo(() => {
    const m = new Map<string, Record<string, number> & { revenue: number }>();
    for (const r of filtered) {
      const d = r.created_at.slice(0, 10);
      if (!m.has(d)) {
        const base: any = { revenue: 0 };
        stepKeys.forEach((k) => (base[k] = 0));
        m.set(d, base);
      }
      const row = m.get(d)!;
      if (stepKeys.includes(r.name)) (row as any)[r.name]++;
      if (r.name === "checkout_completed") row.revenue += revenueOf(r);
    }
    return Array.from(m.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, v]) => ({ date, ...v }));
  }, [filtered, stepKeys]);

  // Per-slot revenue & funnel
  const bySlot = useMemo(() => {
    const m = new Map<string, Record<string, number> & { revenue: number }>();
    for (const r of filtered) {
      const slot = r.slot_start ?? "";
      if (!slot) continue;
      if (!m.has(slot)) {
        const base: any = { revenue: 0 };
        stepKeys.forEach((k) => (base[k] = 0));
        m.set(slot, base);
      }
      const row = m.get(slot)!;
      if (stepKeys.includes(r.name)) (row as any)[r.name]++;
      if (r.name === "checkout_completed") row.revenue += revenueOf(r);
    }
    return Array.from(m.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([slot, v]) => ({ slot, ...v }));
  }, [filtered, stepKeys]);

  // By placement (attribution breakdown)
  const byPlacement = useMemo(() => {
    const m = new Map<string, Record<string, number> & { revenue: number }>();
    for (const r of filtered) {
      const key = r.placement ?? "—";
      if (!m.has(key)) {
        const base: any = { revenue: 0 };
        stepKeys.forEach((k) => (base[k] = 0));
        m.set(key, base);
      }
      const row = m.get(key)!;
      if (stepKeys.includes(r.name)) (row as any)[r.name]++;
      if (r.name === "checkout_completed") row.revenue += revenueOf(r);
    }
    return Array.from(m.entries()).map(([placement, v]) => ({ placement, ...v }));
  }, [filtered, stepKeys]);

  const first = stepKeys[0];
  const last = stepKeys[stepKeys.length - 1];
  const overallCr =
    totals[first] > 0 ? ((totals[last] / totals[first]) * 100).toFixed(1) : "0.0";

  function exportCsv() {
    const csv = toCsv(
      filtered.map((r) => ({
        created_at: r.created_at,
        name: r.name,
        campaign: r.campaign,
        placement: r.placement,
        destination: r.destination,
        slot_start: r.slot_start,
        studio_id: r.studio_id,
        session_id: r.session_id,
        user_id: r.user_id,
        revenue: r.name === "checkout_completed" ? revenueOf(r) : "",
      })),
    );
    downloadCsv(`funnel-${campaign}-${from}-to-${to}.csv`, csv);
  }

  return (
    <AdminShell title={title}>
      {/* Filters */}
      <div className="grid gap-3 md:grid-cols-6 items-end mb-6">
        <div>
          <Label>From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <Label>Placement</Label>
          <Select value={placement} onValueChange={setPlacement}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="__all">All</SelectItem>
              {placements.map((p) => (
                <SelectItem key={p} value={p}>{p || "(none)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Destination</Label>
          <Select value={destination} onValueChange={setDestination}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="__all">All</SelectItem>
              {destinations.map((p) => (
                <SelectItem key={p} value={p}>{p || "(none)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Studio</Label>
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="__all">All</SelectItem>
              {studios.map((p) => (
                <SelectItem key={p} value={p}>
                  {p.length > 8 ? `${p.slice(0, 8)}…` : p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Session contains</Label>
          <Input value={sessionQ} onChange={(e) => setSessionQ(e.target.value)} placeholder="sess_…" />
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <Button onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Apply"}
        </Button>
        <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}>
          Export CSV
        </Button>
      </div>

      {/* Stats with sparklines */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {resolvedSteps.map((s) => (
          <StatSpark
            key={s.key}
            label={s.label}
            value={totals[s.key] ?? 0}
            color={s.color}
            series={byDay.map((d) => (d as any)[s.key] ?? 0)}
          />
        ))}
        <StatSpark
          label="Revenue (completed)"
          value={`$${revenue.toFixed(2)}`}
          color="#facc15"
          series={byDay.map((d) => d.revenue)}
        />
        <StatSpark
          label={`${resolvedSteps[0]?.label} → ${resolvedSteps[resolvedSteps.length - 1]?.label}`}
          value={`${overallCr}%`}
          color="#f472b6"
        />
      </div>

      <div className="text-xs font-mono text-muted-foreground mb-4">
        AOV: ${aov.toFixed(2)} · {filtered.length} events · {studios.length} studios ·{" "}
        {placements.length} placements
      </div>

      {/* Trend chart */}
      <SectionTitle>FUNNEL TREND</SectionTitle>
      <div className="bg-panel border border-border p-4 mb-8 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={byDay}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
            <Legend />
            {resolvedSteps.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue chart */}
      <SectionTitle>REVENUE BY DAY</SectionTitle>
      <div className="bg-panel border border-border p-4 mb-8 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={byDay}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
            <Bar dataKey="revenue" fill="#facc15" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* By placement */}
      <SectionTitle>BY PLACEMENT</SectionTitle>
      <div className="bg-panel border border-border mb-8 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Placement</TableHead>
              {resolvedSteps.map((s) => <TableHead key={s.key}>{s.label}</TableHead>)}
              <TableHead>Revenue</TableHead>
              <TableHead>CR</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {byPlacement.map((r) => {
              const cr =
                (r as any)[first] > 0
                  ? (((r as any)[last] / (r as any)[first]) * 100).toFixed(1)
                  : "0.0";
              return (
                <TableRow key={r.placement}>
                  <TableCell className="font-mono text-xs">{r.placement}</TableCell>
                  {resolvedSteps.map((s) => (
                    <TableCell key={s.key}>{(r as any)[s.key]}</TableCell>
                  ))}
                  <TableCell>${r.revenue.toFixed(2)}</TableCell>
                  <TableCell>{cr}%</TableCell>
                </TableRow>
              );
            })}
            {!byPlacement.length && (
              <TableRow>
                <TableCell colSpan={resolvedSteps.length + 3} className="text-center text-muted-foreground">
                  No events.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* By slot */}
      <SectionTitle>BY BOOKING SLOT</SectionTitle>
      <div className="bg-panel border border-border mb-8 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Slot start (UTC)</TableHead>
              {resolvedSteps.map((s) => <TableHead key={s.key}>{s.label}</TableHead>)}
              <TableHead>Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bySlot.map((r) => (
              <TableRow key={r.slot}>
                <TableCell className="font-mono text-xs">{r.slot}</TableCell>
                {resolvedSteps.map((s) => (
                  <TableCell key={s.key}>{(r as any)[s.key]}</TableCell>
                ))}
                <TableCell>${r.revenue.toFixed(2)}</TableCell>
              </TableRow>
            ))}
            {!bySlot.length && (
              <TableRow>
                <TableCell colSpan={resolvedSteps.length + 2} className="text-center text-muted-foreground">
                  No slot-level events.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Raw drill-down */}
      <SectionTitle>RAW EVENTS</SectionTitle>
      <div className="bg-panel border border-border mb-8 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time (UTC)</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Placement</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead>Session</TableHead>
              <TableHead>Revenue</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.slice(0, 200).map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.created_at.replace("T", " ").slice(0, 19)}</TableCell>
                <TableCell><Badge variant="outline">{r.name}</Badge></TableCell>
                <TableCell className="text-xs">{r.placement ?? "—"}</TableCell>
                <TableCell className="text-xs">{r.destination ?? "—"}</TableCell>
                <TableCell className="font-mono text-xs max-w-[200px] truncate">{r.session_id ?? "—"}</TableCell>
                <TableCell>{r.name === "checkout_completed" ? `$${revenueOf(r).toFixed(2)}` : "—"}</TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => setDrill(r)}>
                    Inspect
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No events in this range.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {filtered.length > 200 && (
          <div className="p-2 text-xs text-muted-foreground text-center">
            Showing first 200 of {filtered.length}. Refine filters or export CSV.
          </div>
        )}
      </div>

      {/* Drill dialog */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{drill?.name}</DialogTitle>
          </DialogHeader>
          {drill && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <KV k="time" v={drill.created_at} />
                <KV k="campaign" v={drill.campaign ?? "—"} />
                <KV k="placement" v={drill.placement ?? "—"} />
                <KV k="destination" v={drill.destination ?? "—"} />
                <KV k="studio_id" v={drill.studio_id ?? "—"} />
                <KV k="session_id" v={drill.session_id ?? "—"} />
                <KV k="user_id" v={drill.user_id ?? "—"} />
                <KV k="slot_start" v={drill.slot_start ?? "—"} />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">params</div>
                <pre className="bg-background border border-border p-3 text-xs overflow-auto max-h-80">
{JSON.stringify(drill.params, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

// ---------- small subcomponents ----------
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="font-display tracking-widest text-sm mb-2">{children}</h2>;
}

function StatSpark({
  label, value, color, series,
}: { label: string; value: number | string; color: string; series?: number[] }) {
  const data = (series ?? []).map((y, i) => ({ i, y }));
  return (
    <div className="bg-panel border border-border p-4">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-display mt-1">{value}</div>
      {data.length > 1 && (
        <div className="h-8 mt-2 -mx-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <Area type="monotone" dataKey="y" stroke={color} fill={color} fillOpacity={0.2} strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground">{k}:</span>
      <span className="truncate">{v}</span>
    </div>
  );
}

function uniq(vals: (string | null)[]): string[] {
  return Array.from(new Set(vals.filter((v): v is string => !!v))).sort();
}